import React from "react";
import styled, { css, keyframes } from "styled-components";
import type { Card as TCard } from "@presidents/game-engine/src/types";
import * as SuitIcons from "./icons/Suits";
import Joker from "./icons/Joker";

export const MiniCardBack = styled.div.attrs({ "data-mini-card": "true" })`
  background-image: linear-gradient(
    45deg,
    var(--foreground) 25%,
    var(--background) 25%,
    var(--background) 50%,
    var(--foreground) 50%,
    var(--foreground) 75%,
    var(--background) 75%,
    var(--background) 100%
  );
  background-size: 8px 8px;
  width: 20px;
  height: 26px;
  border-radius: 2px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.075); 0 1px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const dealInKeyframes1 = keyframes`
  from {
    transform: translateY(-16px);
  }

`;
const dealInKeyframes2 = keyframes`
 to {
    opacity: 1
  }

`;
export const CardElement = styled.div<{
  idx?: number;
  isSelected?: boolean;
  canSelect?: boolean;
}>`
  font-family: var(--altFontFamily);
  border: 1px solid var(--secondary);
  display: inline-block;
  width: var(--card-width);
  height: var(--card-height);
  background-color: var(--foreground);
  position: relative;
  overflow: hidden;
  border-radius: 5px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.075),
    0 1px 8px rgba(0, 0, 0, 0.05);
  user-select: none;

  &.spade,
  &.club {
    color: var(--suitBlack);
  }
  &.heart,
  &.diamond {
    color: var(--suitRed);
  }
  &.joker {
    writing-mode: vertical-rl;
    text-orientation: upright;
    line-height: 0.8;
    letter-spacing: -2px;
  }
  touch-action: manipulation;
  cursor: pointer;

  ${({ canSelect, idx = 0 }) =>
    canSelect &&
    css`
      opacity: 0;
      animation: ${dealInKeyframes1} 750ms ease-out 1,
        ${dealInKeyframes2} 500ms ease-out 1;
      animation-delay: ${Math.max(0, idx * 75) + "ms"},
        ${Math.max(0, idx * 75) + "ms"};
      animation-fill-mode: none, forwards;
      transition: transform 0.2s ease-out;
    `}
  ${(props) =>
    props.isSelected &&
    css`
      transform: translateY(calc((var(--spacing-default) - 1px) * -1));
      z-index: 999;
    `}
`;

const ValueAndSmallSuit = styled.span`
  width: max-content;
  height: max-content;
  position: absolute;
  top: 2.5%;
  left: 7.5%;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
  gap: 2px;
  & > * {
  }
`;

const Value = styled.span`
  color: #fff;
  font-size: calc(var(--card-width) / 2.8);
`;

const SuitSmall = styled.span`
  font-size: calc(var(--card-width) / 5);
  width: calc(var(--card-width) / 5);
  height: calc(var(--card-width) / 5);
`;

const Suit = styled.span`
  position: absolute;
  top: 66.667%;
  left: 100%;
  width: 80%;
  height: 80%;
  transform: translate(-85%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  & > svg {
    width: 100%;
  }
  [data-suit="joker"] & {
    opacity: 0.7;
    & > svg {
      max-width: 100%;
    }
  }
`;

const formatValue = (value: TCard["value"]) => {
  if (typeof value === "number") {
    return value;
  }
  if (value === "joker") {
    return "JKR";
  }
  return value[0].toUpperCase();
};

type Props = {
  card: TCard;
  idx?: number;
  isSelected?: boolean;
  canSelect?: boolean;
  owner?: string;
  onClick?: () => void;
};
const Card: React.FC<Props> = ({
  card: { _id, value, suit },
  idx,
  onClick,
  isSelected,
  owner,
  canSelect = true
}) => {
  const icons = {
    club: SuitIcons.Club,
    spade: SuitIcons.Spade,
    heart: SuitIcons.Heart,
    diamond: SuitIcons.Diamond,
    joker: Joker
  } as const;
  const Icon = icons[suit as keyof typeof icons];

  return (
    <CardElement
      id={`card-${_id}`}
      idx={idx}
      isSelected={isSelected}
      canSelect={canSelect}
      data-selected={isSelected ? "selected" : undefined}
      data-id={_id}
      data-value={value}
      data-suit={suit}
      data-owner={owner}
      className={"inplay-card " + suit}
      onClick={onClick}
    >
      <ValueAndSmallSuit>
        <Value>{formatValue(value)}</Value>
        {value !== "joker" && <SuitSmall>{Icon && <Icon />}</SuitSmall>}
      </ValueAndSmallSuit>
      <Suit>{Icon && <Icon />}</Suit>
    </CardElement>
  );
};

export default Card;
